import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { routes } from '@linqpal/models'
import { BtButtonProps } from '@linqpal/components/src/ui'
import { PlaidLinkOptions, usePlaidLink } from 'react-plaid-link'
import { useStore } from '../../store'

interface Props extends BtButtonProps {
  onConnectSuccess?: () => void
  text?: string
  setLoading?: (arg: boolean) => void
}

export type PlaidConnectButtonRef = {
  createToken: (arg1?: string, arg2?: string) => void
}

export const PlaidConnectButton = forwardRef<PlaidConnectButtonRef, Props>(
  ({ onConnectSuccess, setLoading = () => {} }, ref) => {
    const {
      userStore,
      screensStore: { paymentMethodsStore },
    } = useStore()
    const [linkToken, setLinkToken] = useState<string>('')
    const [plaidBankId, setPlaidBankId] = useState('')

    const createToken = useCallback(
      (plaidId, highlightRountingNumber) => {
        if (plaidId) setPlaidBankId(plaidId)
        else setPlaidBankId('')
        setLoading(true)
        routes.plaid
          .createLinkToken(highlightRountingNumber, plaidId)
          .then((res) => {
            setLinkToken(res.link_token)
          })
          .finally(() => {
            setLoading(false)
          })
      },
      [setLoading],
    )

    useImperativeHandle(
      ref,
      () => ({
        createToken,
      }),
      [createToken],
    )

    const onSuccess = useCallback(
      async (publicToken, metadata) => {
        setLoading(true)
        console.log(
          `ONSUCCESS: Metadata ${JSON.stringify(metadata)} ${publicToken}`,
        )
        if (plaidBankId) {
          routes.plaid
            .healthCheck(plaidBankId)
            .then(async () => {
              userStore.fetchUser()
              await paymentMethodsStore.fetchPaymentMethods()
              onConnectSuccess && onConnectSuccess()
            })
            .finally(() => setLoading(false))
        } else {
          routes.plaid
            .exchangePublicToken(publicToken)
            .then(async () => {
              userStore.fetchUser()
              await paymentMethodsStore.fetchPaymentMethods()
              onConnectSuccess && onConnectSuccess()
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false))
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [plaidBankId],
    )

    const config: PlaidLinkOptions = useMemo(
      () => ({
        token: linkToken,
        onSuccess,
        onExit: (err, metadata) => {
          console.log(
            `Exited early. Error: ${JSON.stringify(
              err,
            )} Metadata: ${JSON.stringify(metadata)}`,
          )
        },
        onEvent: (eventName, metadata) => {
          console.log(
            `Event ${eventName}, Metadata: ${JSON.stringify(metadata)}`,
          )
        },
      }),
      [linkToken, onSuccess],
    )

    const { open, ready } = usePlaidLink(config)

    useEffect(() => {
      !!ready && !!open && open()
    }, [open, ready])

    return null
  },
)
